import { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical, ChevronRight, Pencil, Star } from 'lucide-react';
import {
  usePipelines,
  useCreatePipeline,
  useUpdatePipeline,
  useDeletePipeline,
  useSetDefaultPipeline,
  useCreateStage,
  useUpdateStage,
  useDeleteStage,
} from '@/hooks/usePipelines';
import { useTeamMembers } from '@/hooks/useTeam';
import { Button, Card, Input, Modal, Select, Skeleton } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import type { Pipeline, PipelineStage } from '@/types';

type StageType = 'normal' | 'won' | 'lost';

const STAGE_TYPE_OPTIONS: { value: StageType; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

export default function PipelineSettings() {
  const { data: pipelines, isLoading } = usePipelines();
  const [showAddPipeline, setShowAddPipeline] = useState(false);
  const [expandedPipelineId, setExpandedPipelineId] = useState<string | null>(() => {
    try {
      return localStorage.getItem('pipeline-settings-expanded');
    } catch {
      return null;
    }
  });

  // Persist expanded state to localStorage
  const handleToggle = (id: string) => {
    const next = expandedPipelineId === id ? null : id;
    setExpandedPipelineId(next);
    try {
      if (next) localStorage.setItem('pipeline-settings-expanded', next);
      else localStorage.removeItem('pipeline-settings-expanded');
    } catch { /* ignore */ }
  };

  // Auto-expand the default pipeline only if no saved preference
  useEffect(() => {
    if (pipelines?.length && !expandedPipelineId) {
      const saved = localStorage.getItem('pipeline-settings-expanded');
      if (saved && pipelines.some((p) => p.id === saved)) return;
      const def = pipelines.find((p) => p.isDefault) || pipelines[0];
      if (def) handleToggle(def.id);
    }
  }, [pipelines]);

  if (isLoading) {
    return <Skeleton className="h-64 w-full rounded-xl" />;
  }

  if (!pipelines?.length) {
    return (
      <Card padding="md">
        <p className="text-body-sm text-grey-500">No pipeline found.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-heading-2 text-secondary">Pipelines</h2>
          <p className="text-body-sm text-grey-500 mt-1">
            Manage your sales pipelines and their stages.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowAddPipeline(true)}>
          <Plus className="w-3.5 h-3.5" />
          New Pipeline
        </Button>
      </div>

      {pipelines.map((pipeline) => (
        <PipelineCard
          key={pipeline.id}
          pipeline={pipeline}
          expanded={expandedPipelineId === pipeline.id}
          onToggle={() => handleToggle(pipeline.id)}
        />
      ))}

      {showAddPipeline && (
        <AddPipelineDialog open={showAddPipeline} onOpenChange={setShowAddPipeline} />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Pipeline Card (collapsible)
   ───────────────────────────────────────────── */

function PipelineCard({
  pipeline,
  expanded,
  onToggle,
}: {
  pipeline: Pipeline;
  expanded: boolean;
  onToggle: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(pipeline.name);
  const [showAddStage, setShowAddStage] = useState(false);
  const updatePipeline = useUpdatePipeline();
  const deletePipeline = useDeletePipeline();
  const setDefault = useSetDefaultPipeline();

  const stages = [...pipeline.stages].sort((a, b) => a.position - b.position);
  const dealCount = pipeline._count?.deals ?? 0;

  const handleRename = async () => {
    if (!name.trim()) return;
    await updatePipeline.mutateAsync({ id: pipeline.id, data: { name: name.trim() } });
    setEditing(false);
  };

  const handleDelete = async () => {
    if (!confirm(`Delete pipeline "${pipeline.name}"? This cannot be undone.`)) return;
    try {
      await deletePipeline.mutateAsync(pipeline.id);
    } catch {
      alert('Cannot delete this pipeline. It may have deals assigned or is the default.');
    }
  };

  const handleSetDefault = async () => {
    await setDefault.mutateAsync(pipeline.id);
  };

  return (
    <Card padding="md">
      {/* Header */}
      <div
        className="flex items-center justify-between cursor-pointer select-none"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <ChevronRight
            className={`w-4 h-4 text-grey-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />

          {editing ? (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <input
                className="input py-1 px-2 text-sm w-48"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleRename()}
                autoFocus
              />
              <Button size="sm" onClick={handleRename} loading={updatePipeline.isPending}>
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setName(pipeline.name); }}>
                Cancel
              </Button>
            </div>
          ) : (
            <h3 className="text-sm font-semibold text-secondary">{pipeline.name}</h3>
          )}

          {pipeline.isDefault && (
            <Badge variant="primary" size="sm">Default</Badge>
          )}

          <span className="text-xs text-grey-400">
            {stages.length} stage{stages.length !== 1 ? 's' : ''}
            {dealCount > 0 && <> &middot; {dealCount} deal{dealCount !== 1 ? 's' : ''}</>}
          </span>
        </div>

        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {!pipeline.isDefault && (
            <button
              onClick={handleSetDefault}
              className="text-xs text-grey-500 hover:text-primary px-2 py-1 rounded hover:bg-grey-100 transition-colors"
              aria-label="Set as default pipeline"
            >
              <Star className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={() => setEditing(true)}
            className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-grey-600 transition-colors"
            aria-label="Rename pipeline"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          {!pipeline.isDefault && dealCount === 0 && (
            <button
              onClick={handleDelete}
              className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-error transition-colors"
              aria-label="Delete pipeline"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Expanded: default owner + stages */}
      {expanded && (
        <div className="mt-4 border-t border-grey-100 pt-4">
          <DefaultOwnerSelector pipeline={pipeline} />

          <div className="flex items-center justify-between mb-3 mt-4">
            <span className="text-xs font-medium text-grey-500 uppercase tracking-wider">
              Stages
            </span>
            <Button size="sm" variant="ghost" onClick={() => setShowAddStage(true)}>
              <Plus className="w-3 h-3" />
              Add Stage
            </Button>
          </div>

          {stages.length === 0 ? (
            <p className="text-sm text-grey-400 py-4 text-center">
              No stages yet. Add stages to start using this pipeline.
            </p>
          ) : (
            <div className="space-y-2">
              {stages.map((stage) => (
                <StageRow key={stage.id} stage={stage} pipelineId={pipeline.id} />
              ))}
            </div>
          )}
        </div>
      )}

      {showAddStage && (
        <AddStageDialog
          pipelineId={pipeline.id}
          nextPosition={stages.length}
          open={showAddStage}
          onOpenChange={setShowAddStage}
        />
      )}
    </Card>
  );
}

/* ─────────────────────────────────────────────
   Default Owner Selector
   ───────────────────────────────────────────── */

function DefaultOwnerSelector({ pipeline }: { pipeline: Pipeline }) {
  const { data: teamMembers } = useTeamMembers();
  const updatePipeline = useUpdatePipeline();

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    await updatePipeline.mutateAsync({
      id: pipeline.id,
      data: { defaultOwnerId: value || null },
    });
  };

  return (
    <div className="flex items-center gap-3">
      <Select
        label="Default Deal Owner"
        value={pipeline.defaultOwnerId ?? ''}
        onChange={handleChange}
        options={[
          { value: '', label: 'None (assigned to creator)' },
          ...(teamMembers ?? []).map((m) => ({
            value: m.id,
            label: m.name || m.email,
          })),
        ]}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────
   Stage Row
   ───────────────────────────────────────────── */

function StageRow({ stage, pipelineId }: { stage: PipelineStage; pipelineId: string }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(stage.name);
  const [color, setColor] = useState(stage.color);
  const [probability, setProbability] = useState(String(stage.winProbability));
  const [stageType, setStageType] = useState<StageType>(
    stage.isTerminal ? (stage.isWon ? 'won' : 'lost') : 'normal',
  );
  const updateStage = useUpdateStage();
  const deleteStage = useDeleteStage();

  const handleStageTypeChange = (type: StageType) => {
    setStageType(type);
    if (type === 'won') setProbability('100');
    else if (type === 'lost') setProbability('0');
  };

  const handleSave = async () => {
    await updateStage.mutateAsync({
      pipelineId,
      stageId: stage.id,
      data: {
        name,
        color,
        winProbability: Number(probability),
        isTerminal: stageType !== 'normal',
        isWon: stageType === 'won',
      },
    });
    setEditing(false);
  };

  const handleDelete = async () => {
    if (!confirm(`Delete stage "${stage.name}"? This only works if no deals are in this stage.`)) return;
    try {
      await deleteStage.mutateAsync({ pipelineId, stageId: stage.id });
    } catch {
      alert('Cannot delete: there are deals in this stage.');
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-grey-50 group">
      <GripVertical className="w-4 h-4 text-grey-300 cursor-grab" />

      <div
        className="w-4 h-4 rounded-full flex-shrink-0 border border-grey-200"
        style={{ backgroundColor: stage.color }}
      />

      {editing ? (
        <div className="flex items-center gap-2 flex-1">
          <input
            className="input py-1 px-2 text-sm w-40"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="w-8 h-8 rounded cursor-pointer"
          />
          <Select
            aria-label="Stage type"
            selectSize="sm"
            options={STAGE_TYPE_OPTIONS}
            value={stageType}
            onChange={(e) => handleStageTypeChange(e.target.value as StageType)}
          />
          <input
            type="number"
            className="input py-1 px-2 text-sm w-20"
            value={probability}
            onChange={(e) => setProbability(e.target.value)}
            min="0"
            max="100"
          />
          <span className="text-xs text-grey-500">%</span>
          <Button size="sm" onClick={handleSave} loading={updateStage.isPending}>
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <>
          <span className="text-sm font-medium text-secondary flex-1">{stage.name}</span>
          <span className="text-xs text-grey-500">{stage.winProbability}%</span>
          {stage.isTerminal && (
            <Badge variant={stage.isWon ? 'success' : 'error'} size="sm">
              {stage.isWon ? 'Won' : 'Closed'}
            </Badge>
          )}
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
            <button
              onClick={() => setEditing(true)}
              className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-grey-600"
            >
              <span className="text-xs">Edit</span>
            </button>
            {!stage.isTerminal && (
              <button
                onClick={handleDelete}
                className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-error"
                aria-label="Delete stage"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Add Pipeline Dialog
   ───────────────────────────────────────────── */

function AddPipelineDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState('');
  const createPipeline = useCreatePipeline();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createPipeline.mutateAsync({ name: name.trim() });
    onOpenChange(false);
    setName('');
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="New Pipeline"
      description="Create a new sales pipeline with its own stages."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-pipeline-form"
            loading={createPipeline.isPending}
            disabled={!name.trim()}
          >
            Create Pipeline
          </Button>
        </>
      }
    >
      <form id="add-pipeline-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Pipeline Name"
          required
          placeholder='e.g. "Existing Clients" or "Partnerships"'
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </form>
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   Add Stage Dialog
   ───────────────────────────────────────────── */

function AddStageDialog({
  pipelineId,
  nextPosition,
  open,
  onOpenChange,
}: {
  pipelineId: string;
  nextPosition: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [form, setForm] = useState({
    name: '',
    color: '#50baee',
    winProbability: '0',
    stageType: 'normal' as StageType,
  });
  const createStage = useCreateStage();

  const handleStageTypeChange = (type: StageType) => {
    setForm((prev) => ({
      ...prev,
      stageType: type,
      winProbability: type === 'won' ? '100' : type === 'lost' ? '0' : prev.winProbability,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createStage.mutateAsync({
      pipelineId,
      data: {
        name: form.name,
        position: nextPosition,
        color: form.color,
        winProbability: Number(form.winProbability),
        isTerminal: form.stageType !== 'normal',
        isWon: form.stageType === 'won',
      },
    });
    onOpenChange(false);
    setForm({ name: '', color: '#50baee', winProbability: '0', stageType: 'normal' });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add Stage"
      description="Add a new stage to your pipeline."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="add-stage-form" loading={createStage.isPending}>
            Add Stage
          </Button>
        </>
      }
    >
      <form id="add-stage-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Stage Name"
          required
          placeholder="e.g. Discovery"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="label" htmlFor="add-stage-color">Color</label>
            <input
              id="add-stage-color"
              type="color"
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              className="w-full h-10 rounded-lg cursor-pointer border border-grey-200"
            />
          </div>
          <Select
            label="Stage Type"
            options={STAGE_TYPE_OPTIONS}
            value={form.stageType}
            onChange={(e) => handleStageTypeChange(e.target.value as StageType)}
          />
          <Input
            label="Win Probability (%)"
            type="number"
            min="0"
            max="100"
            value={form.winProbability}
            onChange={(e) => setForm({ ...form, winProbability: e.target.value })}
          />
        </div>
      </form>
    </Modal>
  );
}
